import React, { useState, useEffect, useMemo } from "react";
import locale from "localization/en";
import styles from "./shift-records-details-cash-and-non-cash.module.scss";
import { Title, Image, Dropdown, Field, Button, Text, TextField } from "components/commons";
import { FormMode } from "enums";
import { RemoveIcon, StocqClockIn } from "images";
import { useForm, useApi, useModal, useAlert } from "hooks";
import { Icon } from "@material-ui/core";
import { formatAmount, formatAmountV2, parseNumber } from "utils";
import { sumBy, find } from "lodash";
import Big from "big.js";
import { InputAmount } from "components/field";
import grid from "styles/grid.module.scss";
import { ShiftStatus } from "enums";
import TooltipForm from "components/commons/tooltip-form/tooltip-form.module.js";
import initialState from "./shift-details-non-cash-state/shift-details-cash-non-cash-net-cash.state";
import { updateShiftById } from "apis/shift-records.api";
import { SuccessModal } from "components/modals";
import {
  CASH_VARIANCE_ANCHOR_ID,
  computeCashVariance,
  employeeShoulderKey,
  employeeToPayAnchorId,
  isShortageCovered,
  isShoulderRequired,
} from "../shift-records-details-completion";

const ShiftDetailsNetCashSummary = ({
  shift,
  pageMode,
  employees,
  setEmployees,
  safeDrop,
  employeesToPay,
  setEmployeesToPay,
  variance,
  setVariance,
  beginningBalance,
  setBeginningBalance,
}) => {
  const successModal = useModal();
  const { expense } = shift;
  const status = shift?.shift.status;
  const totalExpenses = sumBy(expense, "amount") || 0;
  const { beginningCashBalance, shiftId, remarksCashBalance } = shift.shift;
  const { addAlert } = useAlert();

  // const transactionsItemDetails = useMemo(() => {
  //   const result = [];
  //   Object.entries(shift.transactionItem).forEach(([key, productTransaction]) => {
  //     if (!key.toLowerCase().includes("item") && isArray(productTransaction)) {
  //       result.push(...productTransaction.filter((t) => !t.transaction.adjustmentType));
  //     }
  //   });

  //   return result;
  // }, [shift.transactionItem]);

  // const mapCashTransaction = (cashItem) => {
  //   const transactionItem = transactionsItemDetails.find(
  //     (sale) =>
  //       sale.transactionId === cashItem.transactionId &&
  //       sale.transactionItemId === cashItem.transactionItemId,
  //   );

  //   let totalAmount = cashItem.totalAmount - cashItem.excessAmount;
  //   if (cashItem.type === "fuel") {
  //     totalAmount = transactionItem?.originalTotalAmount ?? totalAmount;
  //   }

  //   let finalAmount = totalAmount - cashItem.discount;
  //   let discount = cashItem.discount;

  //   const pumpNumber = Number(String(transactionItem.pump).split(" ")[1]);
  //   const pumpDiscount = shift.shiftDiscounts.find(
  //     (discount) =>
  //       discount.product === nameToSku(cashItem.name) && Number(discount.pumpSlot) === pumpNumber,
  //   );

  //   const totalPumpDiscount =
  //     transactionItem.discount === ""
  //       ? cashItem.vat === 0
  //         ? ((pumpDiscount?.discountPerLiter || 0) / 1.12) * cashItem.quantity
  //         : (pumpDiscount?.discountPerLiter || 0) * cashItem.quantity
  //       : 0;
  //   const vatAmount = cashItem.vat === 0 ? totalAmount - totalAmount / 1.12 : 0;
  //   const promoDiscount =
  //     transactionItem?.discount !== "" && cashItem.vat !== 0 ? cashItem.discount : 0;

  //   discount = Math.round((totalPumpDiscount + vatAmount + promoDiscount) * 100) / 100;
  //   finalAmount = totalAmount - discount;
  //   if (cashItem.type === "lpg" && cashItem.vat === 0) {
  //     discount = totalAmount - cashItem.finalAmountPaid;
  //     finalAmount = totalAmount - discount;
  //   }

  //   return { ...cashItem, totalAmount, finalAmount, discount };
  // };

  const cashTransactions = shift?.transactionPaymentMethod?.cash || [];
  const cashRefunds = (shift?.transactionPaymentMethod?.refund || [])
    .filter((r) => r.method === "cash")
    .map((r) => ({
      ...r,
      discount: Big(r.discount).add(r.vatAmount).toNumber(),
    }));
  const netCashSales = Big(cashTransactions.reduce((sum, t) => Big(sum).add(t.finalAmount), 0))
    .add(cashRefunds.reduce((sum, r) => Big(sum).add(r.finalAmountPaid), 0))
    .round(2);

  const initialFormState = useMemo(
    () => initialState(beginningBalance, remarksCashBalance),
    [beginningBalance, remarksCashBalance],
  );
  const { fields, modifyField, isFormSubmittable, getFormValues } = useForm({
    initialState: initialFormState,
  });

  const formValues = getFormValues();
  const beginningChanged =
    Number(formValues.beginningCashBalance) !== Number(initialFormState.beginningCashBalance.value);
  const remarksChanged =
    String(formValues.remarksCashBalance || "") !==
    String(initialFormState.remarksCashBalance.value || "");

  const isFirstUpdate = !initialFormState.remarksCashBalance.value;
  const hasChanges = isFirstUpdate ? isFormSubmittable : beginningChanged || remarksChanged;
  const confirmDisabled = !hasChanges;

  const updateShift = useApi({
    api: updateShiftById,
    handleOwnError: true,
    params: {
      shiftId,
    },
  });
  const [showTooltip, setShowTooltip] = useState(false);
  const [showRemarksTooltip, setShowRemarksTooltip] = useState(false);

  const totalSafeDrop = sumBy(safeDrop, "totalAmount") || 0;

  const cashOnHand = Number(Big(beginningBalance || 0).add(netCashSales).minus(totalExpenses));

  const totalVariance = computeCashVariance({ totalSafeDrop, cashOnHand });

  useEffect(() => {
    setVariance(totalVariance);
  }, [totalVariance, setVariance]);

  const handleBeginningBalanceBlur = () => {
    const fieldInput = fields?.beginningCashBalance?.value;
    const savedValue = initialFormState.beginningCashBalance.value;
    const inputNum = Number(fieldInput ?? 0);
    const savedNum = Number(savedValue ?? 0);
    const hasUpdatedValue = fieldInput != null && inputNum !== savedNum;

    setShowTooltip(hasUpdatedValue);
  };

  const handleCancel = () => {
    setShowTooltip(false);
  };

  const handleViewRemarks = () => {
    setShowRemarksTooltip(true);
  };

  const handleConfirm = async () => {
    setShowTooltip(false);
    try {
      const params = getFormValues();
      const total = Number(
        Big(totalSafeDrop)
          .minus(params.beginningCashBalance)
          .add(netCashSales)
          .minus(totalExpenses),
      );
      const payload = {
        beginningCashBalance: Number(params.beginningCashBalance),
        remarksCashBalance: params.remarksCashBalance,
      };
      if (total > 0) {
        payload.excess = total;
        payload.shortage = 0;
      } else if (total < 0) {
        payload.excess = 0;
        payload.shortage = Math.abs(total);
      } else {
        payload.excess = 0;
        payload.shortage = 0;
      }
      await updateShift.request(payload);

      const updatedCashOnHand = Number(
        Big(params.beginningCashBalance).add(netCashSales).minus(totalExpenses),
      );
      setBeginningBalance(Number(params.beginningCashBalance));
      setVariance(computeCashVariance({ totalSafeDrop, cashOnHand: updatedCashOnHand }));

      successModal.show();
    } catch (error) {
      return addAlert({
        title: locale.errorWhileUpdatingBeginningBalance,
        subtitle: error?.data?.message,
        type: "error",
        interval: 10000,
      });
    }
  };

  const formatNetCash = ({ data }) => {
    return [
      {
        label: locale.beginningBalance,
        value:
          pageMode === FormMode.Edit ? (
            <TooltipForm
              content={
                <div>
                  <div className={styles.tooltipContent}>
                    {locale.thisUpdateWillOnlyApplyinBOS}
                    <br />
                    <br />
                    {locale.areYouSureYouWantToUpdateBeginningBalance}
                  </div>
                  <Field
                    {...fields.remarksCashBalance}
                    required
                    labelClassName={styles.fieldLabel}
                    label={
                      <>
                        {locale.remarks}
                        <span style={{ color: "red", marginLeft: 2 }}>*</span>
                      </>
                    }
                  >
                    <TextField
                      multiline
                      required
                      {...fields?.remarksCashBalance}
                      onChange={modifyField}
                      rowsMax={3}
                    />
                  </Field>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                    <Button outline style={{ marginRight: 8 }} onClick={handleCancel}>
                      {locale.cancel}
                    </Button>
                    <Button primary disabled={confirmDisabled} onClick={handleConfirm}>
                      {locale.confirm}
                    </Button>
                  </div>
                </div>
              }
              disabled={!showTooltip}
              visible={showTooltip}
            >
              <InputAmount
                {...fields?.beginningCashBalance}
                onChange={modifyField}
                innerLabel
                onBlur={handleBeginningBalanceBlur}
              />
            </TooltipForm>
          ) : (
            <div className={styles.viewBeginningBalance}>
              {(beginningCashBalance > 0 ? "+ " : beginningCashBalance < 0 ? "- ₱" : "") +
                formatAmount(beginningCashBalance)}
              {fields?.remarksCashBalance.value && (
                <TooltipForm
                  content={
                    <div className={styles.viewBeginningBalanceContainer}>
                      <Text strong color={"black"}>
                        {locale.remarks}
                      </Text>
                      <Text label color={"black"} className={styles.remarksText}>
                        {fields?.remarksCashBalance?.value}
                      </Text>
                    </div>
                  }
                  disabled={!showRemarksTooltip}
                  visible={showRemarksTooltip}
                >
                  <span
                    onMouseLeave={() => setShowRemarksTooltip(false)}
                    onMouseEnter={() => setShowRemarksTooltip(true)}
                    style={{ display: "inline-block" }}
                  >
                    <Button link onClick={handleViewRemarks}>
                      {locale.viewRemarks}
                    </Button>
                  </span>
                </TooltipForm>
              )}
            </div>
          ),
      },
      {
        label: locale.cashSales,
        value:
          (netCashSales > 0 ? "+ " : netCashSales < 0 ? "- ₱" : "") +
          formatAmount(netCashSales, ""),
      },
      {
        label: locale.expenses,
        value:
          (totalExpenses > 0 ? "- " : totalExpenses < 0 ? "₱" : "") + formatAmount(totalExpenses),
      },
      {
        label: locale.expectedCashOnHand,
        value: formatAmountV2(cashOnHand, cashOnHand < 0),
      },
      {
        label: locale.actualCashOnHand,
        value: formatAmountV2(totalSafeDrop, totalSafeDrop < 0),
      },
      {
        label: locale.variance,
        value:
          (totalVariance > 0 ? "+ ₱" : totalVariance < 0 || cashOnHand < 0 ? "- ₱" : "") +
          formatAmount(totalVariance, ""),
      },
    ];
  };

  const netCashSummaryData = formatNetCash({ data: shift?.shift });

  const addRow = () => {
    if (options.length > 0) {
      setEmployeesToPay([
        ...employeesToPay,
        {
          label: "",
          value: "",
          shoulderAmount: 0,
          amountCheck: false,
        },
      ]);
    }
  };

  const onRemove = (i) => {
    const newForm = [...employeesToPay];
    newForm.splice(i, 1);
    setEmployeesToPay(newForm);
  };

  const options = employees
    .map((employee, idx) => ({ employee, idx }))
    .filter(({ employee: a }) => a.name !== "" && a.agency !== "" && a.label !== "")
    .map(({ employee, idx }) => ({
      value: employeeShoulderKey(employee, idx),
      label: employee.name,
    }));

  const onChangeAmount = (value, i) => {
    const newForm = [...employeesToPay];
    let inValid = true;
    newForm[i]["shoulderAmount"] = value;

    if (value.length > 0) {
      inValid = false;
    }
    newForm[i].amountCheck = inValid;

    setEmployeesToPay(newForm);
  };

  const onChangeName = (value, i) => {
    const newForm = [...employeesToPay];
    let inValid = false;
    if (find(employeesToPay, (ep) => ep.value === value)) {
      inValid = true;
    } else {
      const option = options.find((o) => o.value === value);
      newForm[i]["label"] = option ? option.label : value;
      newForm[i]["value"] = value;
    }

    newForm[i].nameCheck = inValid;

    setEmployeesToPay(newForm);
  };

  const isEmployeeToPayRequired = pageMode === FormMode.Edit && isShoulderRequired(variance);

  const totalShoulderAmount = useMemo(
    () =>
      employeesToPay.reduce(
        (total, row) => Number(Big(total).add(parseNumber(row.shoulderAmount) || 0)),
        0,
      ),
    [employeesToPay],
  );
  const isShortageUncovered =
    isEmployeeToPayRequired && !isShortageCovered({ variance, totalShoulderAmount });

  return (
    <>
      <Title small black>
        {locale.netCashSummary}
      </Title>
      <div className={styles.spacer10} />
      <div className={styles.netCashTable}>
        {netCashSummaryData && netCashSummaryData.length > 0 && (
          <table className={styles.table}>
            <tbody>
              {netCashSummaryData.map((item, i) => {
                return (
                  <tr key={i}>
                    <td>
                      <Text className={styles.label}>{item.label ?? ""}</Text>
                    </td>

                    <td>
                      <Text
                        className={
                          item.label === locale.variance
                            ? variance < 0 || cashOnHand < 0
                              ? styles.negative
                              : variance > 0
                                ? styles.positive
                                : styles.content
                            : styles.content
                        }
                      >
                        {item.value ?? "0"}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {pageMode === FormMode.View && (
        <>
          <div className={styles.spacer20} />
          {status !== ShiftStatus.Started && variance < 0 && (
            <div className={styles.container}>
              <div className={grid.row}>
                <div className={grid.column}>
                  <Text className={styles.label}>{locale.employeeToShoulderShortage}</Text>
                </div>
                <div className={grid.column}>
                  <Text className={styles.label}>{locale.amountToShoulder}</Text>
                </div>
              </div>
            </div>
          )}
          {status !== ShiftStatus.Started &&
            variance < 0 &&
            employees.map(
              (item, i) =>
                item.shoulderAmount !== 0 && (
                  <div className={styles.container}>
                    <div className={grid.row}>
                      <div className={grid.column}>
                        <Text className={styles.content}>{item.name}</Text>
                      </div>
                      <div className={grid.column}>
                        <Text className={styles.content}>{formatAmount(item.shoulderAmount)}</Text>
                      </div>
                    </div>
                  </div>
                ),
            )}
        </>
      )}

      {Big(variance).lt(0) && pageMode === FormMode.Edit ? (
        <div id={CASH_VARIANCE_ANCHOR_ID}>
          <table className={styles.tableView}>
            <tbody>
              {employeesToPay.map((item, i) => {
                const isEmployeeMissing = isEmployeeToPayRequired && !item.value;
                const hasNameError = isEmployeeMissing || Boolean(item.nameCheck);

                return (
                  <>
                    {options.length > 0 && (
                      <tr key={i}>
                        <td
                          style={{ paddingRight: "20px" }}
                          id={employeeToPayAnchorId(i)}
                          data-incomplete={isEmployeeMissing}
                        >
                          <Field
                            className={styles.content}
                            label={locale.employeeToShoulderShortage}
                            error={hasNameError}
                            message={
                              isEmployeeMissing
                                ? locale.pleaseSelectAnEmployeeToShoulderShortage
                                : item.nameCheck
                                  ? locale.thisEmployeeIsAlreadySelected
                                  : ""
                            }
                          >
                            <Dropdown
                              error={hasNameError}
                              onChange={(value) => {
                                onChangeName(value, i);
                              }}
                              value={item.value}
                              options={options.filter((emp) => {
                                if (emp.value === item.value) return true;
                                const selected = employeesToPay.find((e) => e.value === emp.value);
                                return !selected;
                              })}
                            />
                          </Field>
                        </td>
                        <td className={styles.addOrEditTd}>
                          <Field
                            className={styles.content}
                            label={locale.amountToShoulder}
                            error={Boolean(item.amountCheck) || isShortageUncovered}
                          >
                            <InputAmount
                              innerLabel
                              error={Boolean(item.amountCheck) || isShortageUncovered}
                              label={locale.amount}
                              value={item.shoulderAmount}
                              min={0}
                              max={50000}
                              onChange={(_, { value }) => {
                                onChangeAmount(value, i);
                              }}
                            />
                          </Field>
                        </td>
                        <td>
                          <div>
                            <span onClick={() => onRemove(i)} className={styles.remove}>
                              <Image src={RemoveIcon} />
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
            <div className={styles.spacer10} />
            <Button
              outline
              onClick={addRow}
              startIcon={Icon.Add}
              disabled={options.length === employeesToPay.length || options.length === 0}
            >
              {locale.addEmployee}
            </Button>
          </table>
          {isShortageUncovered && (
            <Text className={styles.shortageError}>
              {locale.totalAmountToShoulderMustCover.replace(
                "{0}",
                formatAmount(Math.abs(variance)).trim(),
              )}
            </Text>
          )}
        </div>
      ) : (
        <></>
      )}
      <div className={styles.spacer50} />

      <SuccessModal
        image={StocqClockIn}
        title={locale.updateBeginningBalance}
        content={locale.successMessageModalBeginningBalance}
        {...successModal}
        close={() => {
          successModal.close();
        }}
        primary={{
          text: locale.gotIt,
          onClick: () => {
            successModal.close();
          },
        }}
      />
    </>
  );
};

export default ShiftDetailsNetCashSummary;
